/**
 * navigation.ts — sentral navigasjons-config med rollebasert tilgang
 *
 * Definerer alle meny-elementer for header-nav og side-nav.
 * Hvert element har `role_ids` som bestemmer hvilke role_id-er som ser det.
 *
 * NB: Vi bruker role_id (number) fra backend — IKKE strenger som 'admin'/'editor'.
 * Backend er kilde til sannhet for hva role_id betyr (se API.md).
 * Per nåværende API.md: 1 = guest, 2 = user, 3 = admin.
 *
 * UiShell filtrerer basert på innlogget brukers role_id via hasRole().
 *
 * Struktur:
 * 1. headerNav — top bar lenker
 * 2. sideNav — sidemeny med lenker og utvidbare menyer
 */

/** Ikonnavn i sidemenyen (øverste nivå). Selve ikonene ligger i components/ui-shell/nav-icons.ts. */
export type NavIconName = 'dashboard' | 'files' | 'rental' | 'plug' | 'ads' | 'crm' | 'admin' | 'settings' | 'logout';

export interface NavItem {
  href: string;
  label: string;
  role_ids: string[];
  icon?: NavIconName;
  /** Avdelingen punktet hører til (navn som i JWT-feltet `role_department_names`). Er den satt, ser brukeren punktet bare hvis rollen har avdelingen. */
  department?: string;
}

export interface NavMenu {
  title: string;
  role_ids: string[];
  /**
   * Avdelingen menyen hører til (navn som i JWT-feltet `role_department_names`). Brukeren ser menyen bare hvis rollens avdelinger inneholder den.
   * Menyer uten `department` vises IKKE, med mindre `alwaysVisible` er satt (User settings). inAds har ingen avdeling og er derfor skjult.
   */
  department?: string;
  /** Vises for alle innloggede uansett avdeling */
  alwaysVisible?: boolean;
  icon?: NavIconName;
  children: NavItem[];
}

export type SideNavItem = NavItem | NavMenu;

// Type guard: sjekker om et element er en meny (har children)
export function isNavMenu(item: SideNavItem): item is NavMenu {
  return 'children' in item;
}

// MIDLERTIDIG: true viser ALLE menypunkt til alle innloggede, uansett rolle (UiShell). Sett til false (eller slett flagget og `canSee` i UiShell.tsx) for å gå tilbake til rollestyrt meny.
export const SHOW_ALL_MENU_ITEMS = true;

// Rolle-ID-er per API.md // DETTE ER DET ENESTE UNNTAKET AV TOLKNING AV APIET
const ROLE_ID_ADMIN = 'f7ae90d2-1432-4d94-b7f5-5739ead57f3f';
const ROLE_ID_USER = '1';
const ROLE_ID_GUEST = '1';
const ALL_ROLES = [ROLE_ID_ADMIN, ROLE_ID_USER, ROLE_ID_GUEST];

// === 1. Header-nav (top bar) ===
export const headerNav: NavItem[] = [
  {
    href: '/dashboard',
    label: 'Dashboard',
    role_ids: [ROLE_ID_ADMIN],
    department: 'inAdmin',
  },
];

// === 2. Side-nav (sidemeny) ===
export const sideNav: SideNavItem[] = [
  {
    href: '/dashboard',
    label: 'inAdmin Dashboard',
    icon: 'dashboard',
    department: 'inAdmin',
    role_ids: [ROLE_ID_ADMIN],
  },
  {
    title: 'inFiles',
    department: 'inFiles',
    icon: 'files',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/jobs',
        label: 'Jobs',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/types',
        label: 'Types',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/kjoretoy',
        label: 'Kjøretøy',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/forhandlere',
        label: 'Forhandlere',
        role_ids: [ROLE_ID_ADMIN],
      },
    ],
  },
  {
    title: 'InRental',
    department: 'inRental',
    icon: 'rental',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/in-rental',
        label: 'Dashboard',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/booking-calendar',
        label: 'Booking Calendar',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/orders',
        label: 'Orders',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/vehicles',
        label: 'Vehicles',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/competitor-prices',
        label: 'Competitor Prices',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/competitor-vehicles',
        label: 'Competitor Vehicles',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/vehicle-groups',
        label: 'Vehicle Groups',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
    ],
  },
  {
    title: 'inPlug',
    department: 'inPlug',
    icon: 'plug',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/in-plug/connections',
        label: 'Connections',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
    ],
  },
  {
    title: 'inAds',
    icon: 'ads',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/in-ads/dashboard',
        label: 'Dashboard',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
    ],
  },
  {
    title: 'inCRM',
    department: 'inCRM',
    icon: 'crm',
    role_ids: ALL_ROLES,
    children: [
      {
        href: '/crm/company',
        label: 'Company',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/crm/persons',
        label: 'Persons',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/crm/customers',
        label: 'Customers',
        role_ids: [ROLE_ID_ADMIN],
      },
    ],
  },
    {
    title: 'inIAM',
    department: 'inIAM',
    icon: 'admin',
    role_ids: [ROLE_ID_ADMIN],
    children: [
      {
        href: '/users',
        label: 'Brukere',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/tenants',
        label: 'Tenants',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/roles',
        label: 'Roller',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/permissions',
        label: 'Tillatelser',
        role_ids: [ROLE_ID_ADMIN],
      },
    ],
  },
  {
    title: 'User settings',
    alwaysVisible: true,
    icon: 'settings',
    role_ids: ALL_ROLES,
    children: [
      {
        href: '/user-settings/webauth',
        label: 'WebAuth',
        role_ids: ALL_ROLES,
      },
    ],
  },
  {
    href: '/logout',
    label: 'Logg ut',
    icon: 'logout',
    role_ids: ALL_ROLES,
  },
];

/**
 * Brukerens startside: første synlige punkt i sidemenyen, ut fra rollens avdelinger (JWT `role_department_names`). Samme regler som sidemenyen:
 * menyer vises når avdelingen finnes (eller `alwaysVisible`), enkeltpunkter med avdeling bare når rollen har den. Utlogging regnes ikke. Alt hentes fra menykonfigurasjonen over, ingenting er hardkodet her.
 */
export function getHomePath(departments: string[]): string {
  for (const item of sideNav) {
    if (isNavMenu(item)) {
      const visible = item.alwaysVisible || (item.department !== undefined && departments.includes(item.department));
      if (visible && item.children.length > 0) return item.children[0].href;
    } else if (item.href !== '/logout' && (!item.department || departments.includes(item.department))) {
      return item.href;
    }
  }
  return '/';
}

/**
 * Er ruten tillatt for rollens avdelinger? Gjelder enkeltpunkter i sidemenyen og toppmenyen som har `department` (f.eks. dashbordet): har ikke rollen avdelingen, er ruten (og alt under) ikke tillatt.
 * Øvrige ruter styres av menyene og backend (403), ikke her.
 */
export function isRouteAllowed(pathname: string, departments: string[]): boolean {
  const gated = [...sideNav.filter((i): i is NavItem => !isNavMenu(i)), ...headerNav].filter((i) => i.department);
  return gated.every((i) => !(pathname === i.href || pathname.startsWith(i.href + '/')) || departments.includes(i.department as string));
}
